import React, { useRef, useState, useEffect } from "react";
import {
    Grid,
    Box,
    Typography,
    Button,
    IconButton,
    CircularProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { CustomTextField } from "components";
import { useAuth } from "context/authContext";
import { UserService } from "services/UserService";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { Timestamp } from "firebase/firestore";

const InsuranceUploaded = ({
    insuranceData,
    setInsuranceData,
    onChange = () => { },
    setShowIcons,
}: any) => {
    const { currentUser } = useAuth();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = useState("");
    const [filePreview, setFilePreview] = useState<string | null>(
        insuranceData.insuranceImageUrl || null
    );
    const [formValues, setFormValues] = useState(insuranceData);
    const [expirationDate, setExpirationDate] = useState<Dayjs | null>(
        insuranceData?.insuranceExpiration
            ? dayjs(insuranceData?.insuranceExpiration)
            : null
    );
    const [isLoading, setLoading] = useState(false);

    /** ✅ Ensures state updates correctly when switching tabs */
    useEffect(() => {

        setFormValues({ ...insuranceData });
        setFilePreview(insuranceData.insuranceImageUrl || null);
    }, [insuranceData]);


    // ✅ Correctly updates licenseData without causing infinite re-renders
    useEffect(() => {
        if (JSON.stringify(insuranceData) !== JSON.stringify(formValues)) {
            setInsuranceData((prevData: any) => ({
                ...prevData,
                ...formValues,
            }));
        }

        setShowIcons((prevState: any) => ({
            ...prevState,
            insurance: !!(
                formValues.insuranceImageUrl &&
                formValues.insuranceExpiration &&
                formValues.insuranceNumber &&
                filePreview
            ),
        }));
    }, [formValues, filePreview, setInsuranceData, setShowIcons]);

    // useEffect(() => {
    //     // Update parent state with form values
    //     setInsuranceData((prevData: any) => ({
    //         ...prevData,
    //         insuranceImageUrl: formValues.insuranceImageUrl,
    //         insuranceExpiration: formValues.insuranceExpiration,
    //         insuranceNumber: formValues.insuranceNumber,
    //     }));

    //     // Update icons visibility
    //     setShowIcons((prevState: any) => ({
    //         ...prevState,
    //         insurance: !!(
    //             formValues.insuranceExpiration &&
    //             formValues.insuranceNumber &&
    //             filePreview
    //         ),
    //     }));
    // }, [formValues]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setInsuranceData((prevData: any) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleUploadInsurance = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };


    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setLoading(true);
        const file = e.target.files?.[0];
        if (file) {
            setFileName(file.name);

            try {
                const downloadURL = await UserService.uploadProfilePicture(
                    currentUser.uid,
                    file
                );

                setFilePreview(downloadURL); // Ensure file preview updates
                setFormValues((prevValues: any) => ({
                    ...prevValues,
                    insuranceImageUrl: downloadURL, // Store the uploaded file URL
                }));

                // Update the main license data
                setInsuranceData((prevData: any) => ({
                    ...prevData,
                    insuranceImageUrl: downloadURL,
                }));

                // Ensure icons visibility updates
                setShowIcons((prevState: any) => ({
                    ...prevState,
                    insurance: true,
                }));
            } catch (error) {
                console.error("Error uploading profile picture:", error);
            } finally {
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
    };

    // Handle expiration date change
    const handleExpirationDateChange = (newDate: Dayjs | null) => {
        setExpirationDate(newDate);
        setInsuranceData((prevData: any) => ({
            ...prevData,
            // insuranceExpiration: newDate ? newDate.toISOString() : '',
            insuranceExpiration: newDate
                ? Timestamp.fromDate(newDate.toDate())
                : null,
        }));
    };

    return (
        <Grid
            display={"flex"}
            gap={5}
            flexDirection={"column"}
            p={{ xs: 2, md: 5 }}
            mt={{ xs: 3, md: 0 }}
        >
            <Grid item xs={12} sm={8} md={6} lg={4}>
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        fontWeight: 700,
                        fontSize: "18px",
                        color: "#000000",
                        marginBottom: 2,
                    }}
                >
                    Please upload your insurance
                </Typography>
                <Box sx={styleSheet.constainer}>
                    {!filePreview ? (
                        <Box sx={styleSheet.downContainer}>
                            <CloudUploadIcon sx={{ fontSize: 40, color: "#666" }} />
                            <Typography variant="body1" sx={{ marginTop: 1 }}>
                                Drag & Drop or choose file to upload
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#666", marginTop: 1 }}>
                                Select an Image
                            </Typography>
                            <Button
                                variant="contained"
                                sx={{
                                    marginTop: 2,
                                    backgroundColor: "#233F91",
                                    color: "#fff",
                                    borderRadius: "15px",
                                    width: "215px",
                                    height: "57px",
                                    mt: 2,
                                }}
                                onClick={handleUploadInsurance}
                            >
                                {isLoading ? <CircularProgress size={24} /> : "Browse File"}
                            </Button>
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                style={{ display: "none" }}
                                onChange={handleFileChange}
                            />
                        </Box>
                    ) : (
                        <Grid display={"flex"} alignItems={"center"} flex={1}>
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: "599px",
                                        height: "51px",
                                        border: "2px dashed #ccc",
                                        borderRadius: "9px",
                                        display: "flex",
                                        alignItems: "center",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    <Typography
                                        ml={2}
                                        fontSize={"18px"}
                                        fontWeight={700}
                                        color={"#595959"}
                                    >
                                        {filePreview}
                                    </Typography>
                                </Box>
                                <IconButton
                                    onClick={() => {
                                        setFilePreview(null);
                                        setFormValues((prev: any) => ({
                                            ...prev,
                                            insuranceImageUrl: "",
                                        }));
                                        setInsuranceData((prevData: any) => ({
                                            ...prevData,
                                            insuranceImageUrl: "",
                                        }));
                                    }}
                                    sx={{ color: "#FF0000" }}
                                >
                                    <img src="assets/icons/iconDelete.png" alt="delete" />
                                </IconButton>
                            </Box>
                        </Grid>
                    )}
                </Box>
            </Grid>
            <Grid
                item
                xs={12}
                sm={6}
                display={"flex"}
                gap={1}
                flexDirection={"column"}
            >
                <label htmlFor="insuranceExpiration" style={styleSheet.labelText}>
                    Insurance Expiration
                </label>
                <Grid display={"flex"} gap={2} alignItems={"center"} flex={1}>
                    <Box sx={{ flexGrow: 1 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                views={["day", "month", "year"]}
                                value={expirationDate}
                                onChange={handleExpirationDateChange}
                                format="MM/DD/YYYY"
                                // disableFuture={false}
                                disablePast
                                slotProps={{
                                    textField: {
                                        inputProps: { readOnly: true }, // ⛔ Prevent manual typing
                                    },
                                }}
                                sx={styleSheet.datePicker}
                            />
                        </LocalizationProvider>
                    </Box>
                </Grid>
            </Grid>
            <Grid
                item
                xs={12}
                sm={6}
                display={"flex"}
                gap={1}
                flexDirection={"column"}
            >
                <label htmlFor="insuranceNumber" style={styleSheet.labelText}>
                    Insurance Number
                </label>
                <Grid display={"flex"} gap={2} alignItems={"center"} flex={1}>
                    <Box sx={{ flexGrow: 1 }}>
                        <CustomTextField
                            name="insuranceNumber"
                            sx={styleSheet.customField}
                            value={insuranceData.insuranceNumber}
                            onChange={handleChange}
                        />
                    </Box>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default InsuranceUploaded;

const styleSheet = {
    labelText: {
        fontWeight: 400,
        fontSize: "17px",
        color: "#000000",
    },
    customField: {
        width: "100%",
        maxWidth: "599px",
    },
    downContainer: {
        border: "2px dashed #ccc",
        width: { xs: "295px", md: "563px" },
        height: "276px",
        borderRadius: "8px",
        padding: "16px",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        backgroundColor: "#f9f9f9",
    },
    constainer: {
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        width: "100%",
        maxWidth: "654px",
    },
    datePicker: {
        width: { xs: "100%", md: "599px" },
        "& .MuiInputBase-root": {
            backgroundColor: "transparent",
            borderRadius: "9px",
            height: "50px",
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#C9C9C9", // Regular border color
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#C9C9C9",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#C9C9C9",
            boxShadow: "0px 0px 0px 4px #C9C9C9",
        },
        "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: "#C9C9C9", // Override the red border color when in error state
        },
        "& .MuiSvgIcon-root": {
            color: "#707070",
        },
    },
};
